import {LobbyImage} from '@colonist/types'
import {UserState} from '../../../../../shared/UserState'
import {DropdownContent} from '../../../../shared/scripts/ui_components/UIComponentDropdownController'
import {Sound} from '../../../../shared/scripts/Sound'
import {UIHeaderProfileDropdownController} from './UIHeaderProfileDropdownController'
import {UIUtilsEjsVariables} from '../../../../shared/scripts/UIUtilsEjsVariables'
import {ViewPopupLogin} from '../../../../shared/scripts/ui_popup/ui_popup_membership_incentive/ViewPopupLogin'
import {setElementInnerHTML, setImageElementAlt} from '../../../../shared/scripts/ui_utils/DomUtils'
import {ClientUserState} from '../../../../shared/scripts/ClientUserState'
import {TranslationObject} from '@colonist/i18n'
import {getLobbyImagePath} from '../../../../shared/scripts/ui_utils/Utils'

interface DiscordUserStateOverrides {
    username: string
    profilePictureUrl: string
    icon: LobbyImage
    karmaStatus: TranslationObject
}

export class UIHeaderProfileController {
    private readonly loginButton: HTMLElement

    private readonly container: HTMLDivElement
    private readonly playerIcon: HTMLImageElement
    private readonly playerMembershipIcon: HTMLImageElement
    username: HTMLElement
    private readonly karma: HTMLElement | null

    private dropdownController: UIHeaderProfileDropdownController
    // eslint-disable-next-line class-methods-use-this
    private getMainPageDropdownContent(): DropdownContent[] {
        return [
            {
                id: 'header_profile_dropdown_profile',
                class: '',
                text: {key: 'strings:homePage.header.viewProfile'},
            }, {
                id: 'header_profile_dropdown_editname',
                class: '',
                text: {key: 'strings:homePage.header.editUsername'},
            }, {
                id: 'header_profile_dropdown_editcolor',
                class: '',
                text: {key: 'strings:homePage.header.defaultColor'},
            }, {
                id: 'header_profile_dropdown_editavatar',
                class: '',
                text: {key: 'strings:homePage.header.editAvatar'},
            }, {
                id: 'header_profile_dropdown_logout',
                class: ClientUserState.userState?.isLoggedIn ? 'component-dropdown-red' : 'component-dropdown-green',
                text: {key: ClientUserState.userState?.isLoggedIn ? 'strings:homePage.header.logout' : 'strings:homePage.header.login'},
            },
        ]
    }

    private readonly discordDropdownContent: DropdownContent[] = [
        {
            id: 'header_profile_dropdown_profile',
            class: '',
            text: {key: 'strings:homePage.header.viewProfile'},
        },
        {
            id: 'header_profile_dropdown_editname',
            class: '',
            text: {key: 'strings:homePage.header.editUsername'},
        }, {
            id: 'header_profile_dropdown_editcolor',
            class: '',
            text: {key: 'strings:homePage.header.defaultColor'},
        }, {
            id: 'header_profile_dropdown_editavatar',
            class: '',
            text: {key: 'strings:homePage.header.editAvatar'},
        },
    ]

    private _discordUserStateOverrides?: DiscordUserStateOverrides
    set discordUserStateOverrides(value: DiscordUserStateOverrides) {
        this._discordUserStateOverrides = value
        this.setState(ClientUserState.userState)
    }

    constructor() {
        this.loginButton = document.getElementById('header_profile_login_button') as HTMLElement

        this.container = document.getElementById('header_profile_items_wrapper') as HTMLDivElement
        this.playerIcon = document.getElementById('header_profile_player_icon') as HTMLImageElement
        this.playerMembershipIcon = document.getElementById('header_profile_membership_icon') as HTMLImageElement
        this.username = document.getElementById('header_profile_username') as HTMLElement
        this.karma = document.getElementById('header_profile_karma')

        UIHeaderProfileController.setVisibilityStates()

        if(this.loginButton != undefined) {
            this.loginButton.onclick = () => {
                Sound.playSound(Sound.SoundType.Click)
                new ViewPopupLogin()
            }
        }

        ClientUserState.registerChangeListener((userState) => {
            this.setState(userState)
        })
    }

    private setState(userState: UserState | undefined) {
        const dropdownParentId = 'header_profile_loggedin_container'
        const dropdownContent = this.getDropdownContent()

        this.dropdownController = new UIHeaderProfileDropdownController(this, dropdownContent, dropdownParentId, this.container)
        document.getElementById(dropdownParentId)?.classList.toggle('hidden', userState == undefined)

        this.loginButton?.classList.toggle('hidden', userState?.isLoggedIn == true)
        this.karma?.classList.toggle('hidden', !userState?.isLoggedIn)

        this.container.classList.toggle('header-profile-items-wrapper-small', !userState?.isLoggedIn)

        this.playerMembershipIcon.style.display = 'none'
        if(userState?.isLoggedIn) {
            this.container.style.display = 'flex'
            const currentMembership = userState.items.getCurrentMembership()
            if(currentMembership != undefined) {
                UIHeaderProfileController.updateIcon(currentMembership.image, this.playerMembershipIcon)
                this.playerMembershipIcon.style.display = 'block'
                if(currentMembership.pending) this.playerMembershipIcon.style.filter = 'grayscale(1)'
            }
        }

        this.dropdownController.defaultColorEditor.setState({
            activeColor: userState?.preferredColor,
            availableColors: userState?.items.getAvailableColors() ?? [],
        })

        const username = this._discordUserStateOverrides?.username ?? userState?.username
        if(this.username.innerHTML != username) setElementInnerHTML(this.username, {key: 'strings:colonistCommon.utils.#buffer', options: {value: username}})
        if(this.karma) {
            const karmaStatus = this._discordUserStateOverrides?.karmaStatus ?? userState?.getPlayerKarmaStatusText()
            if(karmaStatus) setElementInnerHTML(this.karma, karmaStatus)
        }
        const profilePictureUrl = this._discordUserStateOverrides?.profilePictureUrl ?? userState?.profilePictureUrl
        if(profilePictureUrl) {
            UIHeaderProfileController.updateProfilePicture(profilePictureUrl, this.playerIcon)
        } else {
            const icon = this._discordUserStateOverrides?.icon ?? userState?.icon ?? LobbyImage.Guest
            UIHeaderProfileController.updateIcon(icon, this.playerIcon)
        }
    }

    private static updateProfilePicture(imagePath: string, element: HTMLImageElement) {
        element.src = imagePath
        element.style.borderRadius = '100%'
    }

    private static updateIcon(icon: LobbyImage, element: HTMLImageElement) {
        const path = getLobbyImagePath(icon)
        element.src = path
        setImageElementAlt(element, icon)
    }

    private static setVisibilityStates() {
        const dropdownParent = document.getElementById('header_profile_loggedin_container') as HTMLDivElement
        const dropdownArrow = dropdownParent.getElementsByClassName('component-dropdown-arrow')[0] as HTMLAnchorElement
        const dropdownView = dropdownParent.getElementsByClassName('component-dropdown-content')[0] as HTMLDivElement
        dropdownArrow.onmouseover = () => dropdownView.classList.add('show')
        dropdownView.onclick = () => dropdownView.classList.remove('show')
        dropdownParent.onmouseleave = () => dropdownView.classList.remove('show')
    }

    showUpdateUsernamePopup() {
        this.dropdownController.usernameEditor.show()
    }

    private getDropdownContent(): DropdownContent[] {
        if(UIUtilsEjsVariables.isUserOnDiscord == false) return this.getMainPageDropdownContent()
        return this.discordDropdownContent
    }
}
